import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { api } from "./services/api";
import { useHashRoute } from "./hooks/useHashRoute";
import { useToasts } from "./hooks/useToasts";
import AppShell from "./app/AppShell";
import AiOutageBanner from "./app/AiOutageBanner";
import AuthPage from "./pages/AuthPage";
import DashboardPage from "./pages/DashboardPage";
import InquiryPage from "./pages/InquiryPage";
import { LoadingState } from "./components/ui/Feedback";
import RouteErrorBoundary from "./components/ui/RouteErrorBoundary";

// Secondary pages load on demand. Routes mount on nav hover/focus
// (onRouteIntent), so the chunk download starts before the click.
const CHUNK_RELOAD_KEY = "bluesun:chunk-reload";
function lazyPage(loader) {
  return lazy(() => loader()
    .then((module) => {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return module;
    })
    .catch((error) => {
      // A deploy replaces hashed chunks; an old tab must reload once to get
      // the new index.html instead of showing a broken page.
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    }));
}
const MailboxPage = lazyPage(() => import("./pages/MailboxPage"));
const SettingsPage = lazyPage(() => import("./pages/SettingsPage"));
const SystemLogsPage = lazyPage(() => import("./pages/SystemLogsPage"));
const AdminPage = lazyPage(() => import("./pages/AdminPage"));
const KnowledgeCenterPage = lazyPage(() => import("./pages/KnowledgeCenterPage"));
const JoinfCustomersPage = lazyPage(() => import("./pages/JoinfCustomersPage"));
const WhatsAppPage = lazyPage(() => import("./pages/WhatsAppPage"));
import { ToastViewport } from "./components/ui/Toast";

const ROUTES = new Set(["dashboard", "inquiries", "mailbox", "whatsapp", "joinf", "settings", "system_logs", "knowledge", "admin"]);

export default function App() {
  const [route, navigate] = useHashRoute();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardState, setDashboardState] = useState({ loading: false, error: "" });
  const dashboardRequest = useRef(0);
  const dashboardInFlight = useRef(null);
  const [mountedRoutes, setMountedRoutes] = useState(["dashboard"]);
  const { toasts, notify, dismiss } = useToasts();

  useEffect(() => {
    api.me().then(setUser).catch(() => setUser(null)).finally(() => setAuthLoading(false));
  }, []);
  useEffect(() => {
    if (!ROUTES.has(route) || (route === "admin" && user && !user.is_admin)) navigate("dashboard");
  }, [route, user, navigate]);

  const canMountRoute = useCallback((target) => (
    ROUTES.has(target) && (target !== "admin" || Boolean(user?.is_admin))
  ), [user?.is_admin]);

  const mountRoute = useCallback((target) => {
    if (!canMountRoute(target)) return;
    setMountedRoutes((current) => (
      current.includes(target) ? current : [...current, target]
    ));
  }, [canMountRoute]);

  useEffect(() => {
    setMountedRoutes(["dashboard"]);
    dashboardRequest.current += 1;
    dashboardInFlight.current = null;
    setDashboard(null);
    setDashboardState({ loading: false, error: "" });
  }, [user?.id, user?.username]);

  useEffect(() => {
    if (user) mountRoute(route);
  }, [route, user, mountRoute]);

  const loadDashboard = useCallback(async () => {
    if (!user) return;
    if (dashboardInFlight.current?.userId === user.id) return;
    const requestId = ++dashboardRequest.current;
    dashboardInFlight.current = { userId: user.id, requestId };
    setDashboardState((value) => ({ ...value, loading: true, error: "" }));
    try {
      const dashboardData = await api.dashboard();
      if (!Array.isArray(dashboardData.priority_high_value)) {
        const highValueThreshold = Number(dashboardData.high_value_min_rating) || 4;
        try {
          const inquiryPage = await api.inquiries({ limit: 100, offset: 0 });
          const visibleItems = inquiryPage.items || [];
          dashboardData.priority_high_value = visibleItems
            .filter((item) => (
              Number(item.business_value_rating) >= highValueThreshold
              && item.status !== "replied"
              && (item.has_ai_research !== false || Boolean(item.ai_research))
            ))
            .sort((left, right) => (
              Number(right.business_value_rating) - Number(left.business_value_rating)
            ))
            .slice(0, 8);
          dashboardData.priority_failed = visibleItems
            .filter((item) => item.status === "failed")
            .slice(0, 8);
        } catch {
          const compatibleItems = [...(dashboardData.priority || []), ...(dashboardData.recent || [])];
          dashboardData.priority_high_value = compatibleItems
            .filter((item) => Number(item.business_value_rating) >= highValueThreshold && item.status !== "replied")
            .sort((left, right) => Number(right.business_value_rating) - Number(left.business_value_rating))
            .slice(0, 8);
          dashboardData.priority_failed = compatibleItems
            .filter((item) => item.status === "failed")
            .slice(0, 8);
        }
      }
      if (requestId !== dashboardRequest.current) return;
      setDashboard({ ...dashboardData, ownerId: user.id });
      setDashboardState({ loading: false, error: "" });
    } catch (error) {
      if (requestId !== dashboardRequest.current) return;
      if (error.status === 401) setUser(null);
      setDashboardState({ loading: false, error: error.message });
    } finally {
      if (dashboardInFlight.current?.requestId === requestId) dashboardInFlight.current = null;
    }
  }, [user]);

  useEffect(() => {
    if (!user || route !== "dashboard") return undefined;
    loadDashboard();
    const refresh = () => { if (!document.hidden) loadDashboard(); };
    const refreshTimer = window.setInterval(refresh, 30_000);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("online", refresh);
    return () => {
      window.clearInterval(refreshTimer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("online", refresh);
      dashboardRequest.current += 1;
      dashboardInFlight.current = null;
    };
  }, [user, route, loadDashboard]);

  const logout = async () => {
    try { await api.logout(); } catch { /* stale sessions can still be cleared locally */ }
    dashboardRequest.current += 1;
    dashboardInFlight.current = null;
    setDashboard(null);
    setUser(null);
    navigate("dashboard");
  };

  if (authLoading) return <div className="bootstrap-screen"><LoadingState label="正在验证登录状态…" compact /></div>;
  if (!user) return <AuthPage onAuthenticated={setUser} />;

  const activeRoute = canMountRoute(route) ? route : "dashboard";
  const renderedRoutes = (
    mountedRoutes.includes(activeRoute) ? mountedRoutes : [...mountedRoutes, activeRoute]
  );

  const renderPage = (target) => {
    const active = target === activeRoute;
    if (target === "inquiries") return <InquiryPage active={active} notify={notify} onDashboardChanged={loadDashboard} />;
    if (target === "mailbox") return <MailboxPage active={active} notify={notify} />;
    if (target === "whatsapp") return <WhatsAppPage active={active} user={user} notify={notify} />;
    if (target === "settings") return <SettingsPage user={user} notify={notify} onSaved={loadDashboard} />;
    if (target === "system_logs") return <SystemLogsPage notify={notify} />;
    if (target === "knowledge") return <KnowledgeCenterPage user={user} notify={notify} />;
    if (target === "joinf") return <JoinfCustomersPage currentUser={user} />;
    if (target === "admin" && user.is_admin) return <AdminPage currentUser={user} notify={notify} />;
    return <DashboardPage key={user.id} active={active} data={dashboard?.ownerId === user.id ? dashboard : null} loading={dashboardState.loading} error={dashboardState.error} reload={loadDashboard} notify={notify} user={user} />;
  };

  return (
    <AppShell route={activeRoute} user={user} onLogout={logout} onRouteIntent={mountRoute}>
      <AiOutageBanner userId={user.id} />
      <div className="route-cache">
        {renderedRoutes.map((target) => (
          <div
            className={`route-panel ${target === activeRoute ? "is-active" : ""}`}
            hidden={target !== activeRoute}
            aria-hidden={target !== activeRoute}
            key={target}
          >
            <RouteErrorBoundary>
              <Suspense fallback={<LoadingState label="正在加载页面…" compact />}>
                {renderPage(target)}
              </Suspense>
            </RouteErrorBoundary>
          </div>
        ))}
      </div>
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </AppShell>
  );
}
